/**
 * cache/broadcast-queue.ts — microtask-coalesced cross-tab broadcast (T7106).
 *
 * **Cross-tab write reconciliation must not amplify under hot load.** Direct
 * `postMessage` on every `onSuccess` + `seedsCache` write would push
 * (100 trades/sec × ~5 cache writes/trade × N sibling tabs) messages onto
 * the channel. Receiver doesn't re-broadcast (no infinite loop), but the
 * deserialization cost is non-trivial on mobile. This queue coalesces by
 * queryKey-hash within a microtask — LAST WRITE WINS for the same key,
 * mirroring the NATS-router patchBuffer pattern.
 *
 * Every local mutation `onSuccess` / `seedsCache` write calls
 * `scheduleBroadcast(queryKey, data, updatedAt)` instead of `postMessage`
 * directly.
 *
 * Failure mode this queue prevents: a 100-trade burst on one market
 * produces 500 `cache:write` messages on the channel; sibling tabs deserialise
 * each one and run a structural-sharing diff, blocking the main thread for
 * ~80ms. With the queue, the burst collapses to ONE message per affected
 * queryKey.
 */

import { hashKey, type QueryKey } from '@tanstack/react-query';

export interface BroadcastChannelLike {
  postMessage(message: unknown): void;
}

export interface BroadcastEntry {
  readonly queryKey: QueryKey;
  readonly data: unknown;
  readonly updatedAt: number;
}

/** Unified with `broadcast-cache-sync.ts` `CacheWriteMessage`. The
 *  `originTabNonce` is both the receiver's echo filter and LWW
 *  tiebreaker — there is no separate `tabNonce`. */
export interface BroadcastMessage {
  readonly type: 'cache:write';
  readonly queryKey: QueryKey;
  readonly data: unknown;
  readonly updatedAt: number;
  readonly originTabNonce: number;
}

export interface BroadcastQueue {
  /** Enqueue a cache write for cross-tab broadcast; coalesces by queryKey-hash. */
  readonly scheduleBroadcast: (queryKey: QueryKey, data: unknown, updatedAt: number) => void;
  /** Drop pending work before the underlying BroadcastChannel is closed. */
  readonly dispose: () => void;
  /** Pending hashes — exported for test visibility, not for runtime use. */
  readonly _pendingCount: () => number;
}

/**
 * Factory: bind a coalescing queue to a `BroadcastChannel`-like target and
 * an origin-tab nonce. The nonce flows through every emitted message so
 * receivers can suppress echoes from their own tab.
 *
 * @param channel BroadcastChannel (or any object with `postMessage`).
 * @param tabNonce stable per-tab id (e.g. `crypto.randomUUID()` → number).
 */
// Devtool peek — single instance per running app, constructed during
// cache setup. Pin on `globalThis` so observability hooks can read
// `_pendingCount()` without provider-context plumbing.
const BCQ_PIN_KEY = '__morfi_broadcast_queue__' as const;
declare global {
  // eslint-disable-next-line no-var
  var __morfi_broadcast_queue__: BroadcastQueue | undefined;
}
export function getBroadcastQueue(): BroadcastQueue | undefined {
  return (globalThis as { [BCQ_PIN_KEY]?: BroadcastQueue })[BCQ_PIN_KEY];
}

export function makeBroadcastQueue(
  channel: BroadcastChannelLike,
  tabNonce: number,
): BroadcastQueue {
  const queue = new Map<string, BroadcastEntry>();
  let scheduled = false;
  let disposed = false;

  function flush(): void {
    scheduled = false;
    if (disposed) return;
    const batch = Array.from(queue.values());
    queue.clear();
    for (const entry of batch) {
      const message: BroadcastMessage = {
        type: 'cache:write',
        queryKey: entry.queryKey,
        data: entry.data,
        updatedAt: entry.updatedAt,
        originTabNonce: tabNonce,
      };
      channel.postMessage(message);
    }
  }

  function scheduleBroadcast(queryKey: QueryKey, data: unknown, updatedAt: number): void {
    if (disposed) return;
    queue.set(hashKey(queryKey), { queryKey, data, updatedAt });
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(flush);
  }

  function dispose(): void {
    disposed = true;
    scheduled = false;
    queue.clear();
  }

  const api: BroadcastQueue = {
    scheduleBroadcast,
    dispose,
    _pendingCount: () => queue.size,
  };
  (globalThis as { [BCQ_PIN_KEY]?: BroadcastQueue })[BCQ_PIN_KEY] = api;
  return api;
}
